import {
  AppShell,
  Burger,
  Flex,
  Header,
  MantineProvider,
  MediaQuery,
} from '@mantine/core';
import {
  NotificationsProvider,
  showNotification,
} from '@mantine/notifications';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { NotificationIndicator } from './components/NotificationIndicator';
import { Sidebar } from './components/Sidebar';
import { UserMenu } from './components/UserMenu';
import { useGetNotifications } from './data/hooks/notifications';
import { useNativeNotification } from './hooks/use-native-notification';
import useNotificationsContext from './hooks/use-notifications-context';
import usePermissionsContext from './hooks/use-permissions-context';
import { useNotificationsSocket } from './hooks/use-notifications-socket';
import { UserRole, UserType } from './models/user';
import { RootState } from './providers/store';
import { Router } from './routes';
import { logoutDispatcher, resetLoadingDispatcher } from './store/auth';
import { AppRoutePaths } from './utils/enums';
import { notificationsRedirectTo } from './utils/notifications-redirect-to';

import { ChatWidget } from './components/ChatWidget';
import { rootNavigate } from './components/CustomRouter';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UpdateBanner } from './components/UpdateBanner';
import { useAppVersion } from './hooks/use-app-version';

const mantineTheme = {
  globalStyles: () => ({
    '*, *::before, *::after': {
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    '::-webkit-scrollbar': {
      width: '10px',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      background: 'rgba(187, 187, 187, 0.5)',
      borderRadius: '6px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(187, 187, 187, 0.8)',
    },
  }),
  fontFamily: 'Roboto, sans-serif',
  headings: {
    fontFamily: 'Roboto, sans-serif',
  },
  colorScheme: 'light' as const,
  colors: {
    ltpBlue: [
      '#EAF0FB',
      '#C4D5F3',
      '#9EBAEB',
      '#789FE3',
      '#5285DB',
      '#2C6AD3',
      '#2355A9',
      '#1A3F7F',
      '#112A55',
      '#09152A',
    ] as any,
  },
};

function App() {
  const { signed, user, loading } = useSelector(
    (state: RootState) => ({
      signed: state.auth.signed,
      user: state.auth.user,
      loading: state.auth.loading,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch<any>();
  const [opened, setOpened] = useState(false);
  const {
    getNotifications,
    readNotifications,
    list,
    setNotifications,
    loadMore,
    hasMore,
    loadingMore,
  } = useNotificationsContext();
  const { sendNotification } = useNativeNotification();
  const { fetch: getNotificationFetcher } = useGetNotifications();
  const { getPermissions } = usePermissionsContext();

  async function handleGetNotifications(userRole: UserRole) {
    await getNotificationFetcher({
      params: { page: 1, limit: 5 },
      onSuccess: (notifications) => {
        if (notifications.length) {
          setNotifications((prev) => {
            const newNotifications = notifications.filter(
              (n) => !prev.some((p) => p.id === n.id),
            );

            for (const item of newNotifications) {
              if (item.type === 'order_price') {
                showNotification({
                  id: `${item.id}`,
                  message: (
                    <div
                      onClick={() => {
                        rootNavigate(AppRoutePaths.FINANCIAL_CHECK);
                        readNotifications([item.id]);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <strong>{item.title}</strong>
                      <p>
                        {item.message ??
                          'Um pedido foi criado via BOLETO acima de R$ 5.000,00'}
                      </p>
                    </div>
                  ),
                  color: 'blue',
                  autoClose: false,
                  onClose: () => readNotifications([item.id]),
                });
              }
              if (item.type === 'material_associated') {
                showNotification({
                  id: `${item.id}`,
                  message: (
                    <div
                      onClick={() => {
                        rootNavigate(AppRoutePaths.ORDER_MATERIALS_PENDING);
                        readNotifications([item.id]);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <strong>{item.title}</strong>
                      <p>{item.message ?? 'Um novo material foi associado'}</p>
                    </div>
                  ),
                  color: 'blue',
                  autoClose: false,
                  onClose: () => readNotifications([item.id]),
                });
              }
              sendNotification({
                title: item.title,
                message: item.message,
                user: user?.username ?? '',
                to: notificationsRedirectTo(item, userRole),
                callback: () => readNotifications([item.id]),
              });
            }

            const merged = [...notifications];
            for (const item of prev) {
              if (!merged.some((n) => n.id === item.id)) {
                merged.push(item);
              }
            }
            return merged.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            );
          });
        }
      },
    });
  }

  useNotificationsSocket((notifications) => {
    if (notifications.length && user) {
      setNotifications((prev) => {
        const newNotifications = notifications.filter(
          (n) => !prev.some((p) => p.id === n.id),
        );

        for (const item of newNotifications) {
          if (item.type === 'order_price') {
            showNotification({
              id: `${item.id}`,
              message: (
                <div
                  onClick={() => {
                    rootNavigate(AppRoutePaths.FINANCIAL_CHECK);
                    readNotifications([item.id]);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <strong>{item.title}</strong>
                  <p>
                    {item.message ??
                      'Um pedido foi criado via BOLETO acima de R$ 5.000,00'}
                  </p>
                </div>
              ),
              color: 'blue',
              autoClose: false,
              onClose: () => readNotifications([item.id]),
            });
          }
          if (item.type === 'material_associated') {
            showNotification({
              id: `${item.id}`,
              message: (
                <div
                  onClick={() => {
                    window.location.replace(
                      AppRoutePaths.ORDER_MATERIALS_PENDING,
                    );
                    readNotifications([item.id]);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <strong>{item.title}</strong>
                  <p>{item.message ?? 'Um novo material foi associado'}</p>
                </div>
              ),
              color: 'blue',
              autoClose: false,
              onClose: () => readNotifications([item.id]),
            });
          }
          sendNotification({
            title: item.title,
            message: item.message,
            user: user?.username ?? '',
            to: notificationsRedirectTo(item, user.role),
            callback: () => readNotifications([item.id]),
          });
        }

        const merged = [...notifications];
        for (const item of prev) {
          if (!merged.some((n) => n.id === item.id)) {
            merged.push(item);
          }
        }
        return merged.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      });
    }
  }, signed);

  useEffect(() => {
    if (signed && user) {
      handleGetNotifications(user.role);
    }
  }, [signed, user?.id]);

  useEffect(() => {
    if (user) {
      getPermissions();
    }
  }, [user?.id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (loading) {
        dispatch(resetLoadingDispatcher());
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const { hasNewVersion, applyUpdate } = useAppVersion();

  const handleLogout = useCallback(
    () => dispatch(logoutDispatcher()),
    [dispatch],
  );

  const handleRead = useCallback(
    (id: number) => readNotifications([id]),
    [readNotifications],
  );

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS theme={mantineTheme}>
      <NotificationsProvider position="top-right" limit={5}>
        <AppShell
          navbarOffsetBreakpoint="sm"
          asideOffsetBreakpoint="sm"
          navbar={
            signed ? (
              <Sidebar opened={opened} setOpened={setOpened} />
            ) : undefined
          }
          header={
            <Header
              height={{
                base: hasNewVersion ? 90 : 50,
                md: hasNewVersion ? 90 : 50,
              }}
              p={0}
            >
              {hasNewVersion && <UpdateBanner onUpdate={applyUpdate} />}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 50,
                  padding: '0 16px',
                }}
              >
                {user && (
                  <MediaQuery largerThan="md" styles={{ display: 'none' }}>
                    <Flex justify="space-between" align="center" w="100%">
                      <Burger
                        hidden={!signed}
                        opened={opened}
                        onClick={() => setOpened((o) => !o)}
                        size="sm"
                        mr="xl"
                      />
                      <Flex gap="sm" align="center">
                        {signed &&
                          (user?.type === UserType.MASTER ||
                            user?.role === UserRole.VENDOR ||
                            user?.role === UserRole.SAC) && <ChatWidget />}
                        <UserMenu user={user} onLogout={handleLogout} />
                      </Flex>
                    </Flex>
                  </MediaQuery>
                )}
                {user && (
                  <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
                    <Flex justify="space-between" align="center" w="100%">
                      <Burger
                        color="none"
                        hidden={!signed}
                        opened={opened}
                        onClick={() => setOpened((o) => !o)}
                        size="sm"
                        mr="xl"
                      />
                      <NotificationIndicator
                        getNotifications={getNotifications}
                        onRead={handleRead}
                        notifications={list}
                        user={user}
                        loadMore={loadMore}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                      />
                      <Flex gap="xl" align="center">
                        {signed &&
                          (user?.type === UserType.MASTER ||
                            user?.role === UserRole.VENDOR ||
                            user?.role === UserRole.SAC) && <ChatWidget />}
                        <UserMenu user={user} onLogout={handleLogout} />
                      </Flex>
                    </Flex>
                  </MediaQuery>
                )}
              </div>
            </Header>
          }
        >
          <ErrorBoundary>
            <Router />
          </ErrorBoundary>
        </AppShell>
      </NotificationsProvider>
    </MantineProvider>
  );
}

export default App;
